import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { COLORS } from 'config/constants';
import { generateSelectorsFromTestIds } from 'helpers';

import Flex from 'components/Flex';
import TextHeader from 'components/TextHeader';
import Tooltip from 'components/Tooltip';

import { getNextStepIndex } from './helpers';
import { WizardStepPropType } from './propTypes';

export const ACTIVE_STEP_COLOR = COLORS.PRIMARY;
const PREVIOUS_STEP_COLOR = COLORS.GRAY_MEDIUM;
const INCOMPLETE_STEP_COLOR = COLORS.GRAY_LIGHT;

const OverviewCrumb = styled(Link)`
  border-right: 1px solid #ccc;
  padding-right: 1.5rem;
  span {
    display: flex;
    align-items: center;
  }
`;

const CrumbWrapper = styled(Link)`
  font-size: 3rem;
  margin-left: 1.5rem;
  &:hover {
    text-decoration: none;
  }
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
  `}
`;

const Crumb = styled.div`
  border-radius: 1rem;
  height: 0.6rem;
  width: 0.6rem;
`;

const crumbColor = ({ active, previousStep }) => {
  if (active) return ACTIVE_STEP_COLOR;
  if (previousStep) return PREVIOUS_STEP_COLOR;
  return INCOMPLETE_STEP_COLOR;
};

const WizardBreadCrumb = ({ steps, stepIndex, overviewPath, title }) => {
  const requiredStepIndex = getNextStepIndex(steps, { required: true });
  if (!steps || steps.length < 2) return null;
  const requiredStep = steps[requiredStepIndex];

  return (
    <Flex
      data-testid={WizardBreadCrumb.TEST_IDS.CONTAINER}
      align="center"
      className="mr-2"
    >
      <OverviewCrumb
        to={location => ({
          ...location,
          pathname: overviewPath
        })}
        data-testid={WizardBreadCrumb.TEST_IDS.OVERVIEW_CRUMB}
      >
        <Tooltip content="Overview" placement="bottom" title="List">
          <TextHeader
            style={{
              color: stepIndex === null ? ACTIVE_STEP_COLOR : PREVIOUS_STEP_COLOR
            }}
            data-testid={WizardBreadCrumb.TEST_IDS.OVERVIEW_CRUMB_TITLE}
            type={TextHeader.TYPES.SMALL}
            className="mb-0"
          >
            {title}
          </TextHeader>
        </Tooltip>
      </OverviewCrumb>

      <Flex align="center">
        {steps.map((step, index) => {
          const isDisabled = !!(requiredStep && requiredStepIndex < index);
          const isPastStep = stepIndex && stepIndex > index;

          return (
            <CrumbWrapper
              disabled={isDisabled}
              as={isDisabled ? 'div' : Link}
              to={
                isDisabled
                  ? undefined
                  : location => ({ ...location, pathname: step.path })
              }
              key={step.id}
              data-testid={WizardBreadCrumb.TEST_IDS.CRUMB_LINK}
            >
              <Tooltip content={step.overviewRow.title} placement="bottom">
                <Crumb
                  style={{
                    backgroundColor: crumbColor({
                      active: index === stepIndex || undefined,
                      previousStep: isPastStep
                    })
                  }}
                  data-testid={WizardBreadCrumb.TEST_IDS.CRUMB}
                />
              </Tooltip>
            </CrumbWrapper>
          );
        })}
      </Flex>
    </Flex>
  );
};

WizardBreadCrumb.TEST_IDS = {
  CONTAINER: 'WizardBreadCrumb',
  CRUMB: 'WizardBreadCrumb-crumb',
  CRUMB_LINK: 'WizardBreadCrumb-crumb-link',
  OVERVIEW_CRUMB: 'WizardBreadCrumb-overview-crumb',
  OVERVIEW_CRUMB_TITLE: 'WizardBreadCrumb-overview-crumb--title'
};

WizardBreadCrumb.SELECTORS = generateSelectorsFromTestIds(WizardBreadCrumb.TEST_IDS);

WizardBreadCrumb.propTypes = {
  steps: PropTypes.arrayOf(WizardStepPropType),
  stepIndex: PropTypes.number,
  title: PropTypes.string,
  overviewPath: PropTypes.string,
  requiredStepIndex: PropTypes.number
};

export default WizardBreadCrumb;
